import {
  dehydrateApolloClient,
  getMaintainedParam,
  initApolloClient,
} from "@/lib/apolloClient";
import {
  FIND_SEARCH_PROJECTS,
  GET_DEVELOPER_PROFILE,
  GET_REAL_ESTATE_PROJECT,
  SEARCH_DEVELOPER_PROJECTS,
} from "@/graphql/queries/realestateProjects";
import { hasId, isPage } from "@/lib/path-resolver-middlewares/category";
import { PARAM_FILTER, PLUS_FILTERS } from "@/common/config";
import { toPairs } from "@/lib/path-resolver-middlewares/filters";
import {
  getCategoryExtendedFields,
  getCategoryFields,
  getQuickFiltersCategoryFields,
  getSearchCategoryFields,
} from "@/src/pages/api/[category]/fields";
import {
  GET_LISTING,
  GET_LISTING_EXTENDED_DETAILS,
} from "../../graphql/queries/listing";
import { getIdFromUrl } from "./listing";
import {
  getDeveloperUrl,
  getProjectURL,
} from "@/containers/project-screen/ProjectCard";

const DEFAULT_VARIABLES = {
  sort: {
    create_time: "desc",
    has_img: "desc",
  },
};

export const ResolveFilters = async (ctx) => {
  /*keyword is search string param for aqar filters*/
  const { default: KEYS } = await import("./ssr/map.categories[name].fields");
  const { categories } = await import(
    "@/lib/path-resolver-middlewares/ssr/categories"
  );
  const { params, keyword, start, end, ...values } = ctx?.query;
  ctx.path = {
    filters: {},
  };
  const keys = Object.keys(values || {}).filter(
    (key) =>
      key === "category" ||
      PLUS_FILTERS[key] ||
      (!PARAM_FILTER[key] && KEYS[key] && values[key]?.includes(",")),
  );
  const filters = keys.reduce((acc, key) => {
    return {
      ...acc,
      [key]: toPairs(values[key]?.split(",")).reduce(
        (acc2, [operation, value]) => ({
          ...acc2,
          [operation]: value,
        }),
        {},
      ),
    };
  }, {});
  if (
    ctx.authState?.user?.paid > 0 ||
    ctx.authState?.user?.manager_id !== ctx.authState?.user?.user_id
  )
    if (!!ctx.path && !!ctx.path.categoriesDictionary) {
      return;
    }
  let fields = {};
  if (filters.category) {
    const category = filters.category.eq;
    fields = {
      searchFields: getSearchCategoryFields(category),
      quickFields: getQuickFiltersCategoryFields(category),
      fields: getCategoryFields(category),
      extendedFields: getCategoryExtendedFields(category),
    };
  }
  ctx.path = {
    ssr: true,
    mode: "red",
    basePath: "/المشاريع-العقارية",
    filters: {
      ...(keyword ? { keyword } : {}),
      ...filters,
    },
    categories: Object.keys(categories)
      .map((key) => categories[key])
      .map((cat, i) => ({ ...cat, index: cat.id > 100 ? cat.id - 100 : i })),
    categoriesDictionary: Object.values(categories).reduce(
      (acc, cat) => ({ ...acc, [cat.id]: cat }),
      {},
    ),
    ...fields,
  };
  return ctx.path;
};
export const RedProjects = {
  ResolveFilters,
  ResolveProjects: async (ctx, $res) => {
    const [pageOrCity, pageOrText, tooLong] = (ctx?.query?.params || []).map(
      decodeURIComponent,
    );

    if (tooLong) return {};
    if (pageOrCity && hasId.test(pageOrCity)) return {};

    if (isPage.test(pageOrCity) && pageOrText) return {};

    const { cities } = await import(
      "@/lib/path-resolver-middlewares/ssr/cities"
    );
    const city = cities[pageOrCity];

    if (!city && pageOrText && !isPage.test(pageOrText)) return {};

    if (isPage.test(pageOrText) && !city) return {};

    if (city) {
      ctx.path.filters.city_id = {
        eq: city?.id,
      };
      ctx.path.city = {
        ...city,
        city_id: city.id,
      };
    }

    const maintainedParam = getMaintainedParam(
      ctx.authState?.user?.cooperative,
    );
    ctx.path.filters = { ...ctx.path.filters, ...maintainedParam };

    const apolloClient = initApolloClient();
    await apolloClient.query({
      query: FIND_SEARCH_PROJECTS,
      variables: {
        bookable_project_filter: {},
        sort: {
          create_time: "desc",
          has_img: "desc",
        },
        from: ((ctx.path.page || 0 || 1) - 1) * 20,
        size: 20,
        where: ctx.path.filters || {},
      },
    });
    ctx.path.page = ctx.path.page || 0;

    return $res(dehydrateApolloClient(apolloClient, {}));
  },
  ResolveDeveloper: async (ctx, $res) => {
    const [developer, tooLong] = ctx?.query?.params || [];
    if (tooLong) return {};
    if (!developer) return {};
    const developerId = getIdFromUrl(developer);
    const apolloClient = initApolloClient();
    const [developerData, projectsData] = await Promise.all([
      apolloClient.query({
        query: GET_DEVELOPER_PROFILE,
        variables: {
          id: parseInt(developerId, 10),
        },
      }),
      apolloClient.query({
        query: SEARCH_DEVELOPER_PROJECTS,
        variables: {
          id: parseInt(developerId, 10),
        },
      }),
    ]);
    const url = encodeURI(
      "/المشاريع-العقارية/" +
        getDeveloperUrl({
          ...developerData?.data?.RealEstateDeveloper?.get,
          id: developerId,
        }),
    );
    if (url !== ctx.req.url) {
      return ctx.redirect(url);
    }
    ctx.path.developer = {
      ...developerData?.data?.RealEstateDeveloper?.get,
      id: developerId,
      projects:
        projectsData?.data?.RealEstateProjects?.searchDeveloperProjects?.list ||
        [],
    };

    return $res(dehydrateApolloClient(apolloClient, {}));
  },
  ResolveProject: async (ctx, $res) => {
    const [developer, project, tooLong] = ctx?.query?.params || [];
    if (tooLong || !project) return {};
    const project_id = getIdFromUrl(project);
    const apolloClient = initApolloClient();
    ctx.path.filters = { project_id: { eq: project_id || project } };

    const maintainedParam = getMaintainedParam(
      ctx.authState?.user?.cooperative,
    );

    const projectRes = await apolloClient.query({
      query: GET_REAL_ESTATE_PROJECT,
      variables: {
        project_id: parseInt(project_id || project, 10),
        where: {
          project_id: { eq: parseInt(project_id || project, 10) },
          published: { eq: 1 },
          ...maintainedParam,
        },
      },
    });
    ctx.path.project = {
      ...projectRes.data?.RealEstateProjects?.getProject,
    };

    if (!project_id) {
      return $res(ctx.redirect(encodeURI(getProjectURL(ctx.path.project))));
    }

    if (tooLong) return {};

    ctx.path.developer = {
      ...projectRes.data?.RealEstateProjects?.getProject?.real_estate_developer,
    };
    return $res(dehydrateApolloClient(apolloClient, {}));
  },
  ResolveProjectUnit: async (ctx, $res) => {
    const [developer, project, unitId, tooLong] = ctx?.query?.params || [];
    if (tooLong || !project || !unitId) return {};
    const apolloClient = initApolloClient();
    try {
      const project_id = getIdFromUrl(project);
      const [projectRes, webListing, extended] = await Promise.all([
        apolloClient.query({
          query: GET_REAL_ESTATE_PROJECT,
          variables: { project_id: parseInt(project_id, 10) },
        }),
        apolloClient.query({
          query: GET_LISTING,
          variables: {
            id: parseInt(unitId, 10),
          },
        }),
        apolloClient.query({
          query: GET_LISTING_EXTENDED_DETAILS,
          variables: {
            id: parseInt(unitId, 10),
          },
        }),
      ]);

      const listing = webListing?.data?.Web?.listing;
      ctx.path.unit = {
        ...listing,
        views: `${listing.views}`,
        __router__: {
          disabled: true,
        },
        extended_details: extended?.data?.Listing?.getListingExtendedDetails,
      };
      ctx.path.project = {
        ...projectRes?.data?.RealEstateProjects?.getProject,
      };
      ctx.path.developer = {
        ...projectRes.data?.RealEstateProjects?.getProject
          ?.real_estate_developer,
      };

      const { getCategoryExtendedFields, getCategoryFields } = await import(
        "../../pages/api/[category]/fields"
      );
      ctx.path.fields = getCategoryFields(listing.category);
      ctx.path.extendedFields = getCategoryExtendedFields(listing.category);
      return $res(dehydrateApolloClient(apolloClient, {}));
    } catch {
      throw "الوحدة غير موجود";
    }
  },
};
